import React, { useEffect, useRef } from "react";

import { Eye } from "lucide-react";

import { FileIcon } from "@/components/ui/file-icon";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";
import { ReactComponent as SortIcon } from "@assets/svg/sort.svg";

import {
  DeleteFileConfirmation,
  RegenerateFileConfirmation,
} from "@components/FilesListDataTable";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { ScrollArea, ScrollBar } from "@components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { formatNullableDateToLocal } from "@utils/dateUtils";

import { formatDocumentCategory } from "../utils/classificationMapper";

type DocumentChronologicalViewProps = {
  files: DocSorterFileSchema[];
  onViewFile: (file: DocSorterFileSchema | null) => void;
  onViewSummary: (file: DocSorterFileSchema) => void;
  isLoading: boolean;
  selectedDocumentId?: string;
  showSummaryPanel?: boolean;
  isMergeMode?: boolean;
  mergeSelectedFiles?: string[];
  onSelectFileForMerge?: (fileId: string, selected: boolean) => void;
  onSelectAllForMerge?: (selected: boolean) => void;
  initialScrollTopRef?: React.MutableRefObject<number>;
  onScrollTopChange?: (top: number) => void;
  // MDRS-420: doctors (expert-view users) can't delete files — defaults to
  // true so callers that don't pass it (most existing tests) keep today's
  // behaviour.
  canManageFiles?: boolean;
};

type DocumentChronologicalViewColumn = {
  id: string;
  header: string;
  accessor: (file: DocSorterFileSchema) => React.ReactNode;
  sortable?: boolean;
  width: string;
};

export const DocumentChronologicalView = ({
  files,
  onViewFile,
  onViewSummary,
  isLoading,
  selectedDocumentId,
  showSummaryPanel = false,
  isMergeMode = false,
  mergeSelectedFiles = [],
  onSelectFileForMerge,
  onSelectAllForMerge,
  initialScrollTopRef,
  onScrollTopChange,
  canManageFiles = true,
}: DocumentChronologicalViewProps) => {
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const hasAppliedInitialScrollRef = useRef(false);

  // MDRS-408: "View Summary" remounts this view (the page swaps its layout
  // tree), which would otherwise reset scroll to the top. Apply the
  // caller-supplied offset once the scroll area actually exists — it's
  // gated behind the loading/empty-state branches above, so it isn't there
  // yet on the first render while files are still loading — and report live
  // scroll position back up so the parent has something to restore next
  // time. `hasAppliedInitialScrollRef` keeps the restore itself one-shot so
  // later re-runs (e.g. the file list changing size) don't yank the user's
  // scroll back to the stale offset.
  useEffect(() => {
    const el = scrollViewportRef.current;
    if (!el) return;
    if (!hasAppliedInitialScrollRef.current && initialScrollTopRef?.current) {
      el.scrollTop = initialScrollTopRef.current;
    }
    hasAppliedInitialScrollRef.current = true;
    const handleScroll = () => onScrollTopChange?.(el.scrollTop);
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, files.length]);

  const columns: DocumentChronologicalViewColumn[] = [
    {
      id: "date",
      header: "Document Date",
      accessor: (file) => formatNullableDateToLocal(file.date),
      // flex-[n] + min-w-0: columns grow/shrink proportionally to share
      // exactly what's left after the shrink-0 spacer, so the row can never
      // request more than 100% of the container's width (unlike fixed
      // fraction widths, which summed past 100% once the spacer was added).
      width: showSummaryPanel ? "flex-[40] min-w-0" : "flex-[20] min-w-0",
    },
    {
      id: "description",
      header: "Description",
      accessor: (file) => (
        <div className="line-clamp-2">
          {file.short_summary || "No description available"}
        </div>
      ),
      width: "flex-[25] min-w-0",
    },
    {
      id: "category",
      header: "Category",
      accessor: (file) => {
        const displayText =
          formatDocumentCategory(file.classification) || "Uncategorized";
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="truncate">{displayText}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{displayText}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      width: "flex-[20] min-w-0",
    },
    {
      id: "name",
      header: "File Name",
      accessor: (file) => (
        <div className="flex items-center gap-2">
          <FileIcon
            fileName={stripBundlePrefix(file.name)}
            className="h-5 w-5 flex-shrink-0"
          />
          <span className="truncate font-medium text-foreground">
            {stripBundlePrefix(file.name)}
          </span>
        </div>
      ),
      width: showSummaryPanel ? "flex-[60] min-w-0" : "flex-[20] min-w-0",
    },
    ...(showSummaryPanel
      ? []
      : ([
          {
            id: "actions",
            header: "Actions",
            accessor: (file) => (
              <div
                className="flex items-center justify-start gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewSummary(file);
                      }}
                      className="h-8 w-8 p-[4px] text-secondary"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View Full Summary</TooltipContent>
                </Tooltip>

                {/* Regenerate */}
                <div onClick={(e) => e.stopPropagation()}>
                  <RegenerateFileConfirmation
                    file={{
                      id: file.id,
                      name: file.name,
                      classification: file.classification || "",
                      date: file.date || undefined,
                      case: file.case,
                    }}
                  />
                </div>

                {canManageFiles && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <DeleteFileConfirmation
                      file={{
                        id: file.id,
                        name: file.name,
                        classification: file.classification || "",
                        date: file.date || undefined,
                        case: file.case,
                      }}
                      iconOnly
                    />
                  </div>
                )}
              </div>
            ),
            // Fixed width, not shrink-to-content: three icon buttons render
            // identically regardless of file name/description length, so an
            // explicit width keeps the header and data cell exactly the same
            // size (shrink-to-content let them diverge — the header's plain
            // "Actions" text sized far narrower than the data row's buttons,
            // throwing off every column after it).
            width: "w-[130px] shrink-0",
          },
        ] as DocumentChronologicalViewColumn[])),
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <LoadingIcon className="mx-auto mb-4 size-8 animate-spin-ccw" />
          <p className="text-sm text-foreground-muted">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-foreground-muted">No documents found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full space-y-4">
      <ScrollArea ref={scrollViewportRef} className="h-full">
        {/* No gap-4 here — the data rows below space their columns purely via
            each cell's own p-3, so a gap here (on top of that same p-3)
            would drift every header progressively further right than its
            column, worse with each column. */}
        <div className="sticky top-0 z-10 flex items-center border-b bg-background p-5">
          {isMergeMode ? (
            <div className="w-8 shrink-0" data-testid="header-spacer">
              <Checkbox
                checked={
                  mergeSelectedFiles.length === files.length && files.length > 0
                }
                onCheckedChange={(checked) => {
                  onSelectAllForMerge?.(checked === true);
                }}
              />
            </div>
          ) : (
            <div className="w-6 shrink-0" data-testid="header-spacer"></div>
          )}

          {columns.map((column) => (
            <div
              key={column.id}
              data-testid={`header-column-${column.id}`}
              className={cn(
                // p-3 matches the data cells' own padding below — without it
                // the header sits flush to the column edge while the data
                // starts 12px in, so labels read as drifted/centered instead
                // of aligned with their column's content.
                "flex justify-start p-3 text-left font-head text-xs font-semibold text-foreground-muted",
                column.width
              )}
            >
              <div className="flex items-center">
                {column.header}
                {column.sortable && (
                  <span className="ml-2 inline-block">
                    <SortIcon className="h-4 w-4 text-foreground-muted" />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3 p-2">
          {files.map((file) => (
            <div
              key={file.id}
              className={cn(
                "mt-[15px] flex cursor-pointer items-center rounded-xl bg-background p-2",
                {
                  "bg-primary-50": selectedDocumentId === file.id,
                  // Cyan wash on hover — same transient hover treatment as
                  // DataTable's rows.
                  "hover:bg-[#E8FDFF]/50": selectedDocumentId !== file.id,
                }
              )}
              onClick={() => {
                if (!isMergeMode) {
                  if (selectedDocumentId === file.id) {
                    onViewFile(null);
                  } else {
                    onViewFile(file);
                  }
                }
              }}
            >
              {isMergeMode ? (
                <div className="w-8 shrink-0 px-2" data-testid="row-spacer">
                  <Checkbox
                    checked={mergeSelectedFiles.includes(file.id)}
                    onCheckedChange={(checked) => {
                      onSelectFileForMerge?.(file.id, checked === true);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ) : (
                <div className="w-6 shrink-0" data-testid="row-spacer"></div>
              )}

              {columns.map((column) => (
                <div
                  key={column.id}
                  data-testid={`row-column-${column.id}`}
                  className={cn(
                    "overflow-hidden p-3 font-head text-[13px] text-foreground",
                    column.width
                  )}
                >
                  {column.accessor(file)}
                </div>
              ))}
            </div>
          ))}
        </div>
        <ScrollBar orientation="vertical" />
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};
